/**
 * Presentational pieces for the Absence Insights dashboard — KPI cards (with
 * YoY badges), the absence-rate trend line, and the monthly SL/CSL volume
 * bars. Split out of AbsenceInsightsView.tsx purely to keep that file under
 * the repo's max-lines lint limit; there's no reuse elsewhere. The org
 * drill-down trend chart and the tenure-band breakdown live in their own
 * files (AbsenceOrgTrendChart.tsx, AbsenceTenureChart.tsx) for the same
 * line-limit reason.
 *
 * Charts use Chart.js (via react-chartjs-2) to match the visual language of
 * the original prototype (finn_absence_dashboard.html).
 */
import { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js';
import type {
  AbsenceType,
  AbsenceInsights,
  AbsenceMonthlyPoint,
} from '../services/absenceApi';
import { InfoTooltip } from './InfoTooltip';

// BarElement is registered here (not rendered here) because this file is
// always imported first — DayOfWeekChart.tsx and MonthlyVolumeChart.tsx both
// render <Bar> without their own registration call, relying on this one.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend);

// FINN brand tokens (@finn-auto/design-system/src/tokens/colors/colors.js) —
// Chart.js datasets take literal color values, not Tailwind classes, so these
// are the hex/rgba equivalents of trustedBlue/asphalt/iron rather than class names.
export const SICK_LEAVE_COLOR = '#0072EA'; // trustedBlue
export const CHILD_CARE_COLOR = '#DFEFFF'; // lightBlue
export const PRIOR_YEAR_COLOR = 'rgba(112,112,112,0.5)'; // iron, muted

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

interface DaysShape {
  sickLeaveDays: number | null;
  childCareDays: number | null;
  totalAbsenceDays: number | null;
}

interface NonNullableDaysShape {
  sickLeaveDays: number;
  childCareDays: number;
  totalAbsenceDays: number;
}

// Overloaded rather than a single nullable signature: the Employees tab's
// data (never GDPR-suppressed, per that requirement) is always non-null at
// its source, so callers there get a plain `number` back without needing a
// null-check that can never actually trigger. Overview-tab shapes (which can
// carry GDPR-suppressed nulls) get the nullable overload instead.
export function pickDays(shape: NonNullableDaysShape, type: AbsenceType): number;
export function pickDays(shape: DaysShape, type: AbsenceType): number | null;
export function pickDays(shape: DaysShape, type: AbsenceType): number | null {
  if (type === 'sl') return shape.sickLeaveDays;
  if (type === 'csl') return shape.childCareDays;
  return shape.totalAbsenceDays;
}

export function pickRatePct(shape: NonNullableDaysShape & { availableWorkingDays: number }, type: AbsenceType): number;
export function pickRatePct(shape: DaysShape & { availableWorkingDays: number | null }, type: AbsenceType): number | null;
export function pickRatePct(shape: DaysShape & { availableWorkingDays: number | null }, type: AbsenceType): number | null {
  const days = pickDays(shape, type);
  if (days === null || shape.availableWorkingDays === null) return null;
  if (!shape.availableWorkingDays) return 0;
  return Math.round((days / shape.availableWorkingDays) * 10000) / 100;
}

export type OrgScopeLevel = 'department' | 'team' | 'subteam';

/** Cascades to one level below the deepest active department/team filter, matching the prototype. */
export function resolveOrgScopeLevel(selectedDepartments: string[], selectedTeams: string[]): OrgScopeLevel {
  if (selectedTeams.length > 0) return 'subteam';
  if (selectedDepartments.length > 0) return 'team';
  return 'department';
}

export function TogglePill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
        active ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-zinc-200 text-zinc-500 hover:border-zinc-300'
      }`}
    >
      {children}
    </button>
  );
}

export function EmptyChartCard({ title, message }: { title: string; message: string }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">{title}</h2>
      <p className="mt-3 text-sm text-zinc-500">{message}</p>
    </div>
  );
}

function YoyBadge({
  current,
  prior,
  hasPriorData,
  lowerIsBetter = true,
  decimals = 1,
  format = 'count',
}: {
  current: number | null;
  prior: number | null;
  hasPriorData: boolean;
  lowerIsBetter?: boolean;
  decimals?: number;
  format?: 'count' | 'pct';
}) {
  // The current value itself was GDPR-suppressed (small group) — the main KPI
  // value is already hidden, so there's nothing meaningful left to compare.
  if (current === null) {
    return (
      <div className="mt-1.5 flex items-center gap-1">
        <span className="rounded bg-cotton px-1 py-0.5 text-[11px] font-medium italic text-steel">
          Hidden for privacy reasons
        </span>
      </div>
    );
  }

  // The prior period genuinely has no rows (not just a coincidental 0), or
  // its own value was suppressed for the same small-group reason — comparing
  // "current vs. 0" or against a hidden number would look like a real trend
  // when it isn't one, so say so plainly instead of a misleading colored delta.
  if (!hasPriorData || prior === null) {
    return (
      <div className="mt-1.5 flex items-center gap-1">
        <span className="rounded bg-cotton px-1 py-0.5 text-[11px] font-medium italic text-steel">
          {prior === null ? 'Previous year hidden for privacy reasons' : 'No prior-year data'}
        </span>
      </div>
    );
  }

  const delta = current - prior;
  const neutral = Math.abs(delta) < (decimals === 0 ? 0.5 : 0.005);
  const better = lowerIsBetter ? delta < 0 : delta > 0;
  const colorClass = neutral
    ? 'bg-cotton text-iron'
    : better
      ? 'bg-lightGreen text-green'
      : 'bg-lightRed text-red';
  const arrow = neutral ? '→' : delta > 0 ? '↑' : '↓';
  const sign = delta > 0 ? '+' : '';
  const priorStr = format === 'pct' ? `${prior.toFixed(decimals)}%` : prior.toFixed(decimals);
  const deltaStr = format === 'pct' ? `${sign}${delta.toFixed(decimals)}pp` : `${sign}${delta.toFixed(decimals)}`;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      <span className="text-[11px] text-iron">Last year: {priorStr}</span>
      <span className={`rounded px-1 py-0.5 text-[11px] font-semibold ${colorClass}`}>
        {arrow} {deltaStr}
      </span>
    </div>
  );
}

// KPI card top-accent — a data-driven mapping so every card (present and
// future) renders its border the same way; add a card's accent here rather
// than styling it ad hoc where it's declared below.
type KpiAccent = 'trustedBlue' | 'orange' | 'none';
const KPI_ACCENT_BORDER: Record<KpiAccent, string> = {
  trustedBlue: 'border-t-trustedBlue',
  orange: 'border-t-orange',
  none: 'border-t-zinc-200',
};

export function SummaryCards({ data, absenceType }: { data: AbsenceInsights; absenceType: AbsenceType }) {
  const { summary, priorYear } = data;
  const currentDays = pickDays(summary, absenceType);
  const priorDays = pickDays(priorYear.summary, absenceType);
  const currentRate = pickRatePct(summary, absenceType);
  const priorRate = pickRatePct(priorYear.summary, absenceType);
  const currentAvg = currentDays === null ? null : summary.employeeCount > 0 ? currentDays / summary.employeeCount : 0;
  const priorAvg =
    priorDays === null ? null : priorYear.summary.employeeCount > 0 ? priorDays / priorYear.summary.employeeCount : 0;
  // periodMonths is 0 only when the prior window matched zero rows in the
  // warehouse — a real absence of data, not a coincidental zero value.
  const hasPriorData = priorYear.summary.periodMonths > 0;

  const cards: {
    label: string;
    definition: string;
    value: string;
    sub?: string;
    accent?: KpiAccent;
    hidden?: boolean;
    yoy: React.ReactNode;
  }[] = [
    {
      label: 'Total Employees',
      definition:
        'Distinct employees active at any point during the selected date range and filters, based on Personio headcount status. Contractors ("Other Employer") and Parental Leave are excluded to match the org\'s standard headcount definition.',
      value: summary.employeeCount.toLocaleString(),
      sub: `Ø ${summary.avgEmployeeCount.toLocaleString()} average`,
      yoy: (
        <YoyBadge
          current={summary.employeeCount}
          prior={priorYear.summary.employeeCount}
          hasPriorData={hasPriorData}
          lowerIsBetter={false}
          decimals={0}
        />
      ),
    },
    {
      label: 'Absence Rate',
      definition:
        'Total absence days (per the Absence Type filter) divided by total available working days, across every employee matching the current filters — the same formula used everywhere else in this dashboard.',
      value: currentRate === null ? 'Hidden for privacy reasons' : `${currentRate.toFixed(1)}%`,
      hidden: currentRate === null,
      accent: 'trustedBlue',
      yoy: <YoyBadge current={currentRate} prior={priorRate} hasPriorData={hasPriorData} decimals={1} format="pct" />,
    },
    {
      label: 'Avg Absence Days / Employee',
      definition:
        'Total Absence Days divided by Total Employees for the selected range — not adjusted for how long each employee was actually active during the period, so a short window can understate this.',
      value: currentAvg === null ? 'Hidden for privacy reasons' : currentAvg.toFixed(1),
      hidden: currentAvg === null,
      yoy: <YoyBadge current={currentAvg} prior={priorAvg} hasPriorData={hasPriorData} decimals={1} />,
    },
    {
      label: 'Total Absence Days',
      definition:
        'Sum of absence days (per the Absence Type filter) across every employee matching the current filters and date range.',
      value: currentDays === null ? 'Hidden for privacy reasons' : currentDays.toLocaleString(),
      hidden: currentDays === null,
      yoy: <YoyBadge current={currentDays} prior={priorDays} hasPriorData={hasPriorData} decimals={0} />,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className={`rounded-xl border border-t-[3px] border-zinc-200 ${KPI_ACCENT_BORDER[c.accent ?? 'none']} bg-white p-4 shadow-sm`}
        >
          <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
            {c.label}
            <InfoTooltip text={c.definition} />
          </p>
          <p
            className={
              c.hidden
                ? 'mt-1.5 text-sm font-medium italic text-zinc-400'
                : 'mt-1.5 text-2xl font-semibold tabular-nums text-zinc-900'
            }
          >
            {c.value}
          </p>
          {c.yoy}
          {c.sub && <p className="mt-1 text-[10px] text-zinc-400">{c.sub}</p>}
        </div>
      ))}
      <p className="col-span-2 -mt-1 text-xs text-zinc-400 lg:col-span-4">
        Across {summary.employeeCount.toLocaleString()} employees active at any point in the trailing{' '}
        {summary.periodMonths} months
      </p>
    </div>
  );
}

export function AbsenceRateTrendChart({
  points,
  priorPoints,
  absenceType,
}: {
  points: AbsenceMonthlyPoint[];
  priorPoints: AbsenceMonthlyPoint[];
  absenceType: AbsenceType;
}) {
  const [showPrior, setShowPrior] = useState(false);

  if (points.length === 0) {
    return <EmptyChartCard title="Absence Rate Trend" message="No absence data in the selected window." />;
  }

  const priorByMonth = new Map(priorPoints.map((p) => [p.month, p]));
  // Parallel to `datasets` below (same push order) — lets the tooltip
  // callback tell "GDPR-suppressed" apart from "genuinely no data" for the
  // exact point under the cursor without re-deriving it from the chart.js
  // internal parsed value.
  const suppressedByDataset: boolean[][] = [points.map((p) => p.suppressed)];
  const datasets: import('chart.js').ChartDataset<'line'>[] = [
    {
      label: 'Current',
      data: points.map((p) => pickRatePct(p, absenceType)),
      borderColor: SICK_LEAVE_COLOR,
      backgroundColor: hexToRgba(SICK_LEAVE_COLOR, 0.08),
      fill: true,
      tension: 0.35,
      pointRadius: 3,
      pointHoverRadius: 5,
    },
  ];
  if (showPrior) {
    suppressedByDataset.push(
      points.map((p) => {
        const prior = priorByMonth.get(p.month);
        return prior ? prior.suppressed : false;
      })
    );
    datasets.push({
      label: 'Last year',
      data: points.map((p) => {
        const prior = priorByMonth.get(p.month);
        return prior ? pickRatePct(prior, absenceType) : null;
      }),
      borderColor: PRIOR_YEAR_COLOR,
      backgroundColor: 'transparent',
      borderDash: [5, 4],
      fill: false,
      tension: 0.35,
      pointRadius: 2,
      spanGaps: false,
    });
  }

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
            Absence Rate Trend
            <InfoTooltip text="Company-wide absence rate (%) per month — total absence days ÷ total available working days for that month, across every employee matching the current filters." />
          </h2>
          <p className="mt-1 text-xs text-zinc-400">Company-wide monthly absence rate (%)</p>
        </div>
        <TogglePill active={showPrior} onClick={() => setShowPrior((v) => !v)}>
          Compare previous year
        </TogglePill>
      </div>
      <div className="mt-4" style={{ height: 220 }}>
        <Line
          data={{ labels: points.map((p) => p.monthLabel), datasets }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: showPrior, position: 'top', labels: { boxWidth: 10 } },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const suppressed = suppressedByDataset[context.datasetIndex]?.[context.dataIndex];
                    if (suppressed) return `${context.dataset.label}: Hidden for privacy reasons`;
                    return `${context.dataset.label}: ${context.formattedValue}%`;
                  },
                },
              },
            },
            scales: {
              y: { beginAtZero: true, ticks: { callback: (v) => `${v}%` } },
            },
          }}
        />
      </div>
    </section>
  );
}
